
import { useState, useEffect } from 'react';
import { Share2, Link2, QrCode, Copy, Check } from 'lucide-react';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle 
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { GamePlan } from '@/types/gamePlanner';
import { useToast } from '@/hooks/use-toast';
import QRCode from 'react-qr-code';
import { useReferralCodes } from '@/hooks/useReferralCodes';
import { createSharedGamePlan, generateShareableUrl } from '@/services/sharing/sharingService';

interface ShareGamePlan2DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gamePlan: GamePlan | null;
}

export function ShareGamePlan2Dialog({ open, onOpenChange, gamePlan }: ShareGamePlan2DialogProps) {
  const { toast } = useToast();
  const { referralCode } = useReferralCodes();
  
  // Local state
  const [shareUrl, setShareUrl] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // Generate share link when dialog opens
  useEffect(() => {
    if (open && gamePlan) {
      setIsGenerating(true);
      setError(null);
      
      // Create an actual share link
      createSharedGamePlan(gamePlan, referralCode || undefined)
        .then(shareId => {
          if (shareId) {
            const url = generateShareableUrl(shareId, 'game-plan', referralCode || undefined);
            setShareUrl(url);

          } else {
            setError('Failed to create share link');
          }
        })
        .catch(err => {
          console.error('Error creating share link:', err);
          setError('Failed to create share link: ' + (err.message || 'Unknown error'));
        })
        .finally(() => {
          setIsGenerating(false);
        });
      
      // Reset copy state when dialog opens
      setCopied(false);
    }
  }, [open, gamePlan, referralCode]);
  
  const copyToClipboard = async () => {
    if (!shareUrl) return;
    
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast({
        title: "Link copied to clipboard",
        description: "Share it with your friends and training partners"
      });
      
      // Reset the copied state after 2 seconds
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast({
        title: "Failed to copy link",
        description: "Please try again or copy it manually",
        variant: "destructive"
      });
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="w-5 h-5" />
            Share Game Plan
          </DialogTitle>
          <DialogDescription>
            Share your game plan with other BJJ practitioners
          </DialogDescription>
        </DialogHeader>
        
        {isGenerating ? (
          <div className="flex flex-col justify-center items-center py-6 space-y-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <p className="text-sm text-muted-foreground">Creating shareable link...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col justify-center items-center py-6 space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-md p-4 text-center">
              <p className="text-red-600 font-medium">Error</p>
              <p className="text-sm text-red-500">{error}</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => onOpenChange(false)}
              >
                Close
              </Button>
            </div>
          </div>
        ) : (
          <>
            <Tabs defaultValue="link" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="link" className="flex items-center">
                  <Link2 className="w-4 h-4 mr-2" />
                  Link
                </TabsTrigger>
                <TabsTrigger value="qrcode" className="flex items-center">
                  <QrCode className="w-4 h-4 mr-2" />
                  QR Code
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="link" className="pt-4">
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Input
                      value={shareUrl}
                      readOnly
                      className="flex-1"
                      placeholder="Generating link..."
                    />
                    <Button 
                      size="icon" 
                      onClick={copyToClipboard}
                      variant="outline"
                      disabled={!shareUrl}
                      title="Copy to clipboard"
                    >
                      {copied ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  
                  <p className="text-sm text-muted-foreground">
                    When others open this link:
                  </p>
                  <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1">
                    <li>Premium users can import with one click</li>
                    <li>Free users will be prompted to upgrade</li>
                    <li>Non-users will be invited to sign up</li>
                  </ul>
                </div>
              </TabsContent>
              
              <TabsContent value="qrcode" className="pt-4">
                <div className="flex flex-col items-center space-y-4">
                  <div className="bg-white p-4 rounded-md">
                    <QRCode value={shareUrl || "https://matmonster.app"} size={180} />
                  </div>
                  <p className="text-sm text-muted-foreground text-center">
                    Scan this QR code to share your game plan
                  </p>
                </div>
              </TabsContent>
            </Tabs>
            
            <Separator />
            <div className="text-center space-y-2">
              <p className="text-sm font-medium">Sharing benefits</p>
              <p className="text-xs text-muted-foreground">
                When others sign up from your shared content, you'll earn referral points
                that can be redeemed for premium features and discounts.
              </p>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
